import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Palette } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';

interface StyleBible {
  style_id: string;
  name?: string;
  description?: string;
  visual_palette?: string;
  lighting?: string;
  lens_camera?: string;
  color_grade?: string;
  mood?: string;
  texture_grain?: string;
  typography?: string;
  source_production_id?: string;
  updated_at?: string;
}

const BLANK = {
  name: '',
  description: '',
  visual_palette: '',
  lighting: '',
  lens_camera: '',
  color_grade: '',
  mood: '',
  texture_grain: '',
  typography: '',
};

export default function StyleCatalog() {
  const [styles, setStyles] = useState<StyleBible[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation(['catalog', 'common']);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const load = () => {
    apiFetch('/api/styles')
      .then((r) => r.json())
      .then((data) => setStyles(data.styles || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/api/styles', { method: 'POST', body: JSON.stringify(draft) });
      setDraft({ ...BLANK });
      setCreating(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    await apiFetch(`/api/styles/${id}`, { method: 'DELETE' });
    setStyles((prev) => prev.filter((s) => s.style_id !== id));
  };

  const field = (label: string, value?: string) =>
    value ? (
      <p className="text-sm">
        <span className="font-medium text-muted-foreground">{label}: </span>
        {value}
      </p>
    ) : null;

  const input = (key: keyof typeof BLANK, placeholder: string) => (
    <input
      className="w-full text-sm border rounded px-2 py-1 bg-background"
      placeholder={placeholder}
      value={draft[key]}
      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
    />
  );

  return (
    <div className="mx-auto max-w-5xl px-8 py-7">
      <>
        <div className="flex items-center gap-2 mb-6">
          <Palette className="h-5 w-5 text-primary" />
          <h2 className="m-0 font-display text-[22px] font-semibold">{t('style.heading')}</h2>
          <HelpDot>{t('style.help')}</HelpDot>
          <span className="text-sm text-faint">{t('style.subtitle')}</span>
          <div className="flex-1" />
          <Button size="sm" onClick={() => setCreating((c) => !c)}>
            <Plus className="mr-1 h-4 w-4" /> {t('style.new')}
          </Button>
        </div>

        {creating && (
          <Card className="mb-4">
            <CardContent className="py-4 space-y-2">
              {input('name', t('style.ph.name'))}
              {input('description', t('style.ph.description'))}
              {input('visual_palette', t('style.ph.visual_palette'))}
              {input('lighting', t('style.ph.lighting'))}
              {input('lens_camera', t('style.ph.lens_camera'))}
              {input('color_grade', t('style.ph.color_grade'))}
              {input('mood', t('style.ph.mood'))}
              {input('texture_grain', t('style.ph.texture_grain'))}
              {input('typography', t('style.ph.typography'))}
              <div className="flex gap-2 pt-1">
                <Button size="sm" disabled={saving || !draft.name.trim()} onClick={save}>
                  {saving ? t('state.saving', { ns: 'common' }) : t('style.save')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCreating(false)}>
                  {t('action.cancel', { ns: 'common' })}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading && <p className="text-muted-foreground">{t('state.loading', { ns: 'common' })}</p>}
        {!loading && styles.length === 0 && !creating && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">{t('style.emptyCreate')}</CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {styles.map((s) => (
            <Card key={s.style_id}>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{s.name || s.style_id}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {s.style_id}
                  </Badge>
                  {s.source_production_id && (
                    <Badge variant="outline" className="text-xs">
                      {t('style.promoted')}
                    </Badge>
                  )}
                  <div className="flex-1" />
                  <button
                    className="text-muted-foreground hover:text-destructive p-1"
                    onClick={() => setPendingDelete(s.style_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {s.description && <p className="text-sm text-muted-foreground">{s.description}</p>}
                {field(t('style.field.visual_palette'), s.visual_palette)}
                {field(t('style.field.lighting'), s.lighting)}
                {field(t('style.field.lens_camera'), s.lens_camera)}
                {field(t('style.field.grade'), s.color_grade)}
                {field(t('style.field.mood'), s.mood)}
                {field(t('style.field.texture_grain'), s.texture_grain)}
                {field(t('style.field.typography'), s.typography)}
              </CardContent>
            </Card>
          ))}
        </div>

        <ConfirmDialog
          open={!!pendingDelete}
          onOpenChange={(o) => !o && setPendingDelete(null)}
          title={t('style.deleteTitle')}
          warning={t('style.deleteWarning')}
          destructive
          confirmLabel={t('style.deleteConfirm')}
          onConfirm={() => pendingDelete && remove(pendingDelete)}
        />
      </>
    </div>
  );
}
